import React from "react";
import PropTypes from "prop-types";

import RSCard from "reactstrap/lib/Card";
import CardBody from "reactstrap/lib/CardBody";
import CardHeader from "reactstrap/lib/CardHeader";
import CardFooter from "reactstrap/lib/CardFooter";
import CardTitle from "reactstrap/lib/CardTitle";
import CardSubtitle from "reactstrap/lib/CardSubtitle";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";
import Collapse from "reactstrap/lib/Collapse";

import WarningYellowOutline from "@druva-apps/orion-icons/lib/WarningYellowOutline";
import MessageBox from "../messageBox";
import Loader from "../loader";
import "./card.scss";
import Icon from "../icon/icon";
import { ArrowRight } from "../../lib/icons";
import { gatherClasses, isFunction, isString } from "../../lib/utils";

class Card extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isCollapsed: props.collapsedByDefault
    };
  }

  componentDidMount() {
    const { title, subtitle, titleAction } = this.props;

    if (!title && (subtitle || titleAction)) {
      // eslint-disable-next-line no-console
      console.warn(
        "Card title is mandatory while providing subtitle and/or title actions"
      );
    }
  }

  static getDerivedStateFromProps(props) {
    if (!props.title && (props.subtitle || props.titleAction)) {
      // eslint-disable-next-line no-console
      console.warn(
        "Card title is mandatory while providing subtitle and/or title actions"
      );
    }
    return null;
  }

  toggleCollapse = e => {
    if (e) {
      e.stopPropagation();
    }

    const { isCollapsed } = this.state;
    const { onCollapse, onExpand } = this.props;

    this.setState(
      {
        isCollapsed: !isCollapsed
      },
      () => {
        setTimeout(() => {
          const { isCollapsed: newIsCollapsed } = this.state;
          if (newIsCollapsed) {
            onCollapse();
          } else {
            onExpand();
          }
        }, 300);
      }
    );
  };

  onCardTitleContainerClick = e => {
    const classes = e.target.classList;
    // if the click called on something other than card title and collapse toggle icon
    // then simple ignore it
    if (
      !classes.contains("oui-card-title") &&
      !classes.contains("oui-card-collapse-toggler")
    ) {
      return;
    }
    this.toggleCollapse(e);
  };

  renderTitle = () => {
    const { title, collapsible } = this.props;

    return (
      <div
        className="oui-card-title-inner"
        onClick={e => {
          if (collapsible) {
            this.onCardTitleContainerClick(e);
          }
        }}
        aria-hidden="true"
      >
        {isString(title) ? (
          <Card.Title title={title} />
        ) : isFunction(title) ? (
          title()
        ) : null}
      </div>
    );
  };

  render() {
    const {
      title,
      titleAction,
      dataTestId,
      fullHeight,
      collapsible,
      subtitle,
      body,
      footer,
      onClick,
      disabled,
      isLoading,
      isError
    } = this.props;
    const { isCollapsed } = this.state;
    const renderOnlyBody = !title;

    let cardBodyContent = (
      <CardBody className={renderOnlyBody ? "oui-pt-2" : ""}>
        {isError ? (
          <MessageBox
            icon={<WarningYellowOutline />}
            secondaryText="Error loading data"
          />
        ) : null}
        {isFunction(body) && !isError ? body() : null}
      </CardBody>
    );

    cardBodyContent = isLoading ? (
      <Loader isLoading className="oui-card-loader">
        {cardBodyContent}
      </Loader>
    ) : (
      cardBodyContent
    );

    return (
      <RSCard
        data-testid={dataTestId}
        className={gatherClasses(
          this.props,
          fullHeight ? "oui-mnh-100" : null,
          collapsible
            ? isCollapsed
              ? "oui-card-collapsible oui-card-collapsed"
              : "oui-card-collapsible oui-card-expanded"
            : "",
          isFunction(onClick) && "oui-hoverable"
        )}
        onClick={onClick}
      >
        {disabled && <div className="oui-disable-overlay" />}
        {title && (
          <CardHeader className="oui-bg-white oui-bb-0">
            {isFunction(titleAction) || collapsible ? (
              <Row>
                <Col className="oui-card-title-container">
                  {this.renderTitle()}
                  {subtitle && isString(title) ? (
                    <CardSubtitle tag="h6" className="text-muted">
                      {subtitle}
                    </CardSubtitle>
                  ) : null}
                </Col>
                <Col className="oui-col-auto oui-card-title-action">
                  {isFunction(titleAction) ? titleAction() : null}
                  {collapsible ? (
                    <Icon
                      src={ArrowRight}
                      width="12px"
                      height="12px"
                      clickable
                      onClick={this.toggleCollapse}
                      className={gatherClasses(
                        null,
                        "oui-card-collapse-toggler"
                      )}
                    />
                  ) : null}
                </Col>
              </Row>
            ) : (
              // eslint-disable-next-line react/jsx-fragments
              <React.Fragment>
                {this.renderTitle()}
                {subtitle && isString(title) ? (
                  <CardSubtitle tag="h6" className="text-muted">
                    {subtitle}
                  </CardSubtitle>
                ) : null}
              </React.Fragment>
            )}
          </CardHeader>
        )}

        {collapsible ? (
          <Collapse isOpen={!isCollapsed}>
            {cardBodyContent}
            {isFunction(footer) ? <CardFooter>{footer()}</CardFooter> : null}
          </Collapse>
        ) : (
          // eslint-disable-next-line react/jsx-fragments
          <React.Fragment>
            {cardBodyContent}
            {isFunction(footer) ? <CardFooter>{footer()}</CardFooter> : null}
          </React.Fragment>
        )}
      </RSCard>
    );
  }
}

Card.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  subtitle: PropTypes.string,
  body: PropTypes.func.isRequired,
  fullHeight: PropTypes.bool,
  titleAction: PropTypes.func,
  collapsible: PropTypes.bool,
  collapsedByDefault: PropTypes.bool,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,
  footer: PropTypes.func,
  dataTestId: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool
};

Card.defaultProps = {
  fullHeight: false,
  collapsible: false,
  collapsedByDefault: false,
  onCollapse: () => {},
  onExpand: () => {},
  dataTestId: "orion-card",
  onClick: null,
  disabled: false,
  isLoading: false,
  isError: false
};

Card.displayName = "OrionCard";

Card.Title = function CTitle(props) {
  const { title } = props;
  return (
    <CardTitle tag="h6" className={gatherClasses(props, "mb-0")}>
      {title}
    </CardTitle>
  );
};

Card.Title.displayName = "OrionCardTitle";

Card.Title.propTypes = {
  title: PropTypes.string
};

Card.Title.defaultProps = {
  title: ""
};

export default Card;
